import React, {
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import cx from 'classnames';

import {
    Button,
    FileDropZone,
    IconInfoCircle,
    IconMusicFile,
    IconUploadCloud,
    IconX,
    TabPanel,
} from '@trqk/components';
import {
    constants,
    locale,
    getFileInputAccept,
    getMaxAudioUploads,
    getSupportedAudioFormatsLabel,
} from '@trqk/utils';

import { MaxAudioBatchExceeded } from '../../toastMessages/uploadAudio';
import { SomeUploadsFailed, PlanExpired } from '../../toastMessages';
import {
    useDetectionTracks,
    useEntities,
    useToast,
    useUploadAudioFiles,
    usePaymentPlanStatus,
} from '../../../hooks';
import { modalWidth } from './constants';
import { FULL_PATHS, HASHES, PATHS } from '../../../constants';
import { AudioAlerts, AudioUploads, DetectionTracks } from './components';

const { SUPPORTED_AUDIO_FILE_TYPES, MAX_AUDIO_UPLOAD_BATCH_SIZE } = constants;

const DropZoneContent = (): JSX.Element => (
    <div className="flex flex-col items-center">
        <IconUploadCloud fillColor="white" width={80} />
        <div className="font-bold text-white font-lg">
            {locale.enUS.UPLOAD_AUDIO.DROP_TO_UPLOAD}
        </div>
    </div>
);

const fileInputAccept = getFileInputAccept(SUPPORTED_AUDIO_FILE_TYPES);

/**
 * Modal that allows the user to manage various aspects of performance detection. Includes tabs for:
 * - Detection Settings: toggle detection on/off on a per-track basis
 * - Uploads: upload audio files for detection
 * - Alerts: errors encountered during audio file upload
 * @param props - {@link ModalContentProps}
 */
export const ManageDetections = ({
    remove,
}: ModalContentProps<Record<string, never>>): JSX.Element => {
    const { add: addToast } = useToast();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const {
        detectionTracks,
        detectionStats,
        loading,
        refresh,
        setDetectionTracks,
    } = useDetectionTracks();
    const { subscriptionSettings } = usePaymentPlanStatus();
    const subscriptionExpirationDate =
        subscriptionSettings?.subscription_expiration_date ?? null;
    const [maxAudioUploads, setMaxAudioUploads] = useState<Maybe<number>>(null);

    useEffect(() => {
        setMaxAudioUploads(
            getMaxAudioUploads(detectionStats.detectionCapacity),
        );
    }, [detectionStats.detectionCapacity]);

    const { handleFiles, uploadTasks, uploadResults } = useUploadAudioFiles();

    const inProgressTasks = useMemo(
        () =>
            uploadTasks.filter((task: UploadAudioTask): boolean => {
                const error = task?.error ?? null;
                const done = task?.done ?? false;

                return !done && error === null;
            }),
        [uploadTasks],
    );

    const failedUploads = uploadResults.filter(
        ({ canceled, success }: UploadAudioFileOutput): boolean =>
            !canceled && !success,
    );

    // Ensure that EntitySelector is set to "All Entities" when modal is open
    const { sendEntitySelectionEvent } = useEntities();

    useEffect(() => {
        sendEntitySelectionEvent({
            type: 'SET_VIEW_ALL',
            value: {
                viewAll: true,
            },
        });
    }, [sendEntitySelectionEvent]);

    // Ensure that modal is closed if the route doesn't match `/detections/control-panel`
    const match = useRouteMatch({
        path: FULL_PATHS.MANAGE_DETECTIONS,
        strict: true,
        sensitive: true,
    });

    useEffect((): void => {
        if (match === null) {
            remove({
                canceled: false,
                success: false,
            });
        }
    }, [match, remove]);

    const addFiles = useCallback((): void => {
        const hasExpiredSubscription = subscriptionExpirationDate
            ? new Date() > new Date(subscriptionExpirationDate)
            : false;

        if (hasExpiredSubscription) {
            addToast({
                kind: 'warning-card',
                RenderContent: PlanExpired,
                renderContentInput: {
                    fileType: 'audio files',
                    onNavigate: () => {
                        remove({
                            canceled: true,
                            success: false,
                        });
                    },
                },
                persist: true,
            });
        } else if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [subscriptionExpirationDate]);

    const history = useHistory();

    const [activeTabKey, setActiveTabKeyPrivate] = useState(
        history.location.hash ?? HASHES.MANAGE_DETECTIONS.DETECTION_SETTINGS,
    );

    const setActiveTabKey = (nextActiveTabKey: string) => {
        setActiveTabKeyPrivate(nextActiveTabKey);

        history.push({
            hash: nextActiveTabKey,
            search: history.location.search,
        });
    };

    const currentAudioUploads = detectionTracks.length + inProgressTasks.length;

    /**
     * Check if user is trying to upload too many files at once.
     *
     * If so, show a warning toast and do not proceed. Otherwise, proceed with upload.
     * @param {File[]} files the files that were selected for upload
     */
    const uploadOrRejectFiles = useCallback(
        (files: File[]): void => {
            const tooManyFiles =
                files.length + inProgressTasks.length >
                MAX_AUDIO_UPLOAD_BATCH_SIZE;

            if (tooManyFiles) {
                addToast({
                    kind: 'warning-card',
                    RenderContent: MaxAudioBatchExceeded,
                    renderContentInput: {
                        currentUploads: inProgressTasks.length,
                    },
                });
            } else {
                const onGroupStopped = (
                    input: OnGroupStoppedInput<
                        FilenameInput,
                        UploadAudioFileOutput
                    >,
                ): void => {
                    if (input.error.length > 0) {
                        addToast({
                            kind: 'warning-card',
                            RenderContent: SomeUploadsFailed,
                            renderContentInput: {
                                groupStoppedInput: input,
                                fileDescriptor: 'audio file',
                            },
                        });
                    }
                };

                handleFiles({
                    currentAudioUploads,
                    files,
                    maxAudioUploads,
                    onGroupStopped,
                    setDetectionTracks,
                });

                setActiveTabKey(HASHES.MANAGE_DETECTIONS.UPLOADS);
            }
        },
        [
            addToast,
            currentAudioUploads,
            handleFiles,
            inProgressTasks,
            maxAudioUploads,
        ],
    );

    const [modalOpenedAt, setModalOpenedAt] = useState<Maybe<number>>(null);

    useEffect(() => {
        if (modalOpenedAt === null) {
            setModalOpenedAt(Date.now());
        }
    }, [modalOpenedAt]);

    const detectionUsageLabel =
        detectionStats.detectionCapacity === null
            ? ''
            : `(${detectionStats.enabled.nonDraftOnly} of ${detectionStats.detectionCapacity})`;

    const handleDrop = useCallback(
        ({ validFiles, invalidFiles }: HandleDropInput): void => {
            const hasExpiredSubscription = subscriptionExpirationDate
                ? new Date() > new Date(subscriptionExpirationDate)
                : false;

            if (hasExpiredSubscription) {
                addToast({
                    kind: 'warning-card',
                    RenderContent: PlanExpired,
                    renderContentInput: {
                        fileType: 'audio files',
                        onNavigate: () => {
                            remove({
                                canceled: true,
                                success: false,
                            });
                        },
                    },
                    persist: true,
                });
            } else if (maxAudioUploads !== null) {
                uploadOrRejectFiles([...validFiles, ...invalidFiles]);
            }
        },
        [maxAudioUploads, subscriptionExpirationDate],
    );

    return (
        <FileDropZone
            addClasses="rounded-lg"
            acceptFileTypes={SUPPORTED_AUDIO_FILE_TYPES}
            DropZoneContent={DropZoneContent}
            handleDrop={handleDrop}
        >
            <div className="bg-white rounded-lg" style={{ width: modalWidth }}>
                <div className="pt-4 pb-1 mr-4 border-l-4 rounded-tl-lg border-secondary-500">
                    <div className="flex justify-end">
                        <Link
                            to={({ search }) => ({
                                pathname: PATHS.DETECTIONS,
                                search,
                            })}
                        >
                            <button
                                className="mt-5 mr-5 respond-to-mouse"
                                type="button"
                                onClick={(): void => {
                                    remove({
                                        canceled: true,
                                        success: false,
                                    });
                                }}
                            >
                                <IconX width={20} fillColor="tertiary-400" />
                            </button>
                        </Link>
                    </div>
                    <div>
                        <div className="mx-12">
                            <h3>{locale.enUS.MANAGE_DETECTIONS.TITLE}</h3>
                            <div className="flex items-end justify-between my-2">
                                <div className="w-2/3 mt-3 text-sm">
                                    <div>
                                        {
                                            locale.enUS.MANAGE_DETECTIONS
                                                .DESCRIPTION.DRAG_DROP_OR_BROWSE
                                                .PART_1
                                        }
                                        {` (${getSupportedAudioFormatsLabel(
                                            'or',
                                        )}) `}
                                        {
                                            locale.enUS.MANAGE_DETECTIONS
                                                .DESCRIPTION.DRAG_DROP_OR_BROWSE
                                                .PART_2
                                        }
                                    </div>
                                </div>
                                <Button
                                    color="primary"
                                    autoWidth={true}
                                    addClasses="flex text-sm font-semibold items-center px-6 respond-to-mouse"
                                    boxShadow={true}
                                    handleClick={addFiles}
                                >
                                    <IconMusicFile
                                        fillColor="tertiary-200"
                                        addClasses="mr-2"
                                    />
                                    {
                                        locale.enUS.MANAGE_DETECTIONS.BUTTONS
                                            .UPLOAD_AUDIO_FILES
                                    }
                                    <input
                                        accept={fileInputAccept}
                                        className="visually-hidden"
                                        id="upload-audio-file-input"
                                        onChange={(e: SyntheticEvent): void => {
                                            e.preventDefault();

                                            if (fileInputRef.current) {
                                                const files = Array.from(
                                                    fileInputRef.current
                                                        ?.files ?? [],
                                                );

                                                uploadOrRejectFiles(files);

                                                // Reset the input's current value so it can be used again.
                                                fileInputRef.current.value = '';
                                            }
                                        }}
                                        ref={fileInputRef}
                                        type="file"
                                        multiple={true}
                                    />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                <TabPanel
                    activeTabKey={activeTabKey}
                    setActiveTabKey={setActiveTabKey}
                    addClasses="px-10"
                    panels={[
                        {
                            key: HASHES.MANAGE_DETECTIONS.DETECTION_SETTINGS,
                            label: (
                                <div>
                                    {
                                        locale.enUS.MANAGE_DETECTIONS.TAB_LABELS
                                            .DETECTION_SETTINGS
                                    }
                                    <span
                                        style={{ minWidth: 65 }}
                                        className="inline-block ml-1 font-semibold"
                                    >
                                        {detectionUsageLabel}
                                    </span>
                                </div>
                            ),
                            contents: (
                                <DetectionTracks
                                    detectionStats={detectionStats}
                                    detectionTracks={detectionTracks}
                                    loading={loading}
                                    modalOpenedAt={modalOpenedAt}
                                    refresh={refresh}
                                    remove={remove}
                                    setDetectionTracks={setDetectionTracks}
                                />
                            ),
                        },
                        {
                            key: HASHES.MANAGE_DETECTIONS.UPLOADS,
                            label: (
                                <div>
                                    {
                                        locale.enUS.MANAGE_DETECTIONS.TAB_LABELS
                                            .UPLOADS
                                    }
                                    <span
                                        style={{ width: 20 }}
                                        className={cx('font-semibold ml-1', {
                                            'text-secondary-500':
                                                inProgressTasks.length > 0,
                                        })}
                                    >
                                        {`(${inProgressTasks.length})`}
                                    </span>
                                </div>
                            ),
                            contents: (
                                <AudioUploads
                                    currentAudioUploads={currentAudioUploads}
                                    detectionStats={detectionStats}
                                    inProgressTasks={inProgressTasks}
                                />
                            ),
                        },
                        {
                            key: HASHES.MANAGE_DETECTIONS.ALERTS,
                            label: (
                                <div>
                                    {
                                        locale.enUS.MANAGE_DETECTIONS.TAB_LABELS
                                            .ALERTS
                                    }
                                    <span
                                        className={cx('font-semibold ml-1', {
                                            'text-quinary-500':
                                                failedUploads.length > 0,
                                        })}
                                    >
                                        {`(${failedUploads.length})`}
                                    </span>
                                </div>
                            ),

                            contents: (
                                <AudioAlerts failedUploads={failedUploads} />
                            ),
                        },
                    ]}
                />
            </div>
        </FileDropZone>
    );
};
